import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useContent, deepMerge } from "@/lib/content-context";
import { DEFAULT_CONTENT } from "@/lib/content";

const PAGE_TABS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "products", label: "Products" },
  { id: "contact", label: "Contact" },
];

export default function ContentEditor() {
  const { content, replace } = useContent();
  const [draft, setDraft] = useState(() => deepMerge(DEFAULT_CONTENT, content));
  const [tab, setTab] = useState("home");
  const [saving, setSaving] = useState(false);

  // Re-seed draft if remote content changes (e.g. on first load).
  useEffect(() => { setDraft(deepMerge(DEFAULT_CONTENT, content)); }, [content]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(content), [draft, content]);

  const setField = (path, value) => {
    setDraft((prev) => writePath(prev, path, value));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      replace(draft);
      toast.success("Content saved. Public site updated.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetSection = () => {
    if (!window.confirm(`Reset the ${tab.toUpperCase()} section to defaults? (Doesn't save until you click Save.)`)) return;
    setDraft((prev) => ({ ...prev, [tab]: DEFAULT_CONTENT[tab] }));
  };

  return (
    <section data-testid="content-editor-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2">Site Content</div>
          <h2 className="font-serif-display text-3xl md:text-4xl">Edit page copy</h2>
          <p className="text-sm text-[#5C5A56] mt-2">
            Changes apply to the public site as soon as you click <strong>Save</strong>.
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="content-reset-section" onClick={resetSection} className="btn-ghost text-sm">
            <RotateCcw size={14} /> Reset section
          </button>
          <button
            data-testid="content-save"
            onClick={save}
            disabled={saving || !dirty}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : <><Save size={15} /> {dirty ? "Save changes" : "Saved"}</>}
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#1A1A1A]/15 mb-8 overflow-x-auto">
        {PAGE_TABS.map((p) => (
          <button
            key={p.id}
            data-testid={`content-tab-${p.id}`}
            onClick={() => setTab(p.id)}
            className={`px-5 py-3 text-sm tracking-wide border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === p.id ? "border-[#C25934] text-[#1A1A1A] font-medium" : "border-transparent text-[#5C5A56] hover:text-[#1A1A1A]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="space-y-10">
        {tab === "home" && <HomeForm draft={draft} setField={setField} setDraft={setDraft} />}
        {tab === "about" && <AboutForm draft={draft} setField={setField} setDraft={setDraft} />}
        {tab === "products" && <ProductsForm draft={draft} setField={setField} setDraft={setDraft} />}
        {tab === "contact" && <ContactForm draft={draft} setField={setField} />}
      </div>
    </section>
  );
}

/* ---------- helpers ---------- */

function writePath(obj, path, value) {
  const keys = path.split(".");
  const out = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = out;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const next = cur[k];
    cur[k] = Array.isArray(next) ? [...next] : { ...next };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return out;
}

function readPath(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
}

function Group({ title, children }) {
  return (
    <fieldset className="border border-[#1A1A1A]/10 bg-[#F8F7F4] p-6 md:p-8">
      <legend className="overline px-2">{title}</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">{children}</div>
    </fieldset>
  );
}

function TextField({ label, path, draft, setField, full, mono, placeholder }) {
  const value = readPath(draft, path) ?? "";
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5A56] mb-2 font-semibold">{label}</label>
      <input
        data-testid={`field-${path}`}
        value={value}
        onChange={(e) => setField(path, e.target.value)}
        placeholder={placeholder}
        className={`input-editorial ${mono ? "font-mono-ed text-xs" : ""}`}
      />
    </div>
  );
}

function TextArea({ label, path, draft, setField, rows = 4 }) {
  const value = readPath(draft, path) ?? "";
  return (
    <div className="md:col-span-2">
      <label className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5A56] mb-2 font-semibold">{label}</label>
      <textarea
        data-testid={`field-${path}`}
        rows={rows}
        value={value}
        onChange={(e) => setField(path, e.target.value)}
        className="textarea-editorial"
      />
    </div>
  );
}

function ArrayEditor({ title, path, draft, setDraft, fields, addItem, minItems = 0 }) {
  const items = readPath(draft, path) || [];
  const update = (idx, key, value) => {
    setDraft((prev) => {
      const arr = [...(readPath(prev, path) || [])];
      arr[idx] = { ...arr[idx], [key]: value };
      return writePath(prev, path, arr);
    });
  };
  const remove = (idx) => {
    if (items.length <= minItems) return toast.error(`Keep at least ${minItems} item${minItems === 1 ? "" : "s"}.`);
    if (!window.confirm("Remove this item?")) return;
    setDraft((prev) => writePath(prev, path, (readPath(prev, path) || []).filter((_, i) => i !== idx)));
  };
  const add = () => {
    setDraft((prev) => writePath(prev, path, [...(readPath(prev, path) || []), addItem()]));
  };

  return (
    <fieldset className="border border-[#1A1A1A]/10 bg-[#F8F7F4] p-6 md:p-8">
      <legend className="overline px-2">{title}</legend>
      <div className="space-y-5">
        {items.map((item, i) => (
          <div key={i} data-testid={`array-item-${path}-${i}`} className="border border-[#1A1A1A]/10 bg-white/40 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-ed text-xs text-[#C25934]">#{String(i + 1).padStart(2, "0")}</div>
              <button
                type="button"
                data-testid={`array-remove-${path}-${i}`}
                onClick={() => remove(i)}
                className="p-1.5 text-[#5C5A56] hover:text-[#C25934] hover:bg-[#EAE6DF]"
                title="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              {fields.map((f) => (
                <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5A56] mb-2 font-semibold">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      data-testid={`array-field-${path}-${i}-${f.key}`}
                      rows={f.rows || 3}
                      value={item?.[f.key] ?? ""}
                      onChange={(e) => update(i, f.key, e.target.value)}
                      className="textarea-editorial"
                    />
                  ) : (
                    <input
                      data-testid={`array-field-${path}-${i}-${f.key}`}
                      value={item?.[f.key] ?? ""}
                      onChange={(e) => update(i, f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className={`input-editorial ${f.mono ? "font-mono-ed text-xs" : ""}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {addItem && (
          <button
            type="button"
            data-testid={`array-add-${path}`}
            onClick={add}
            className="btn-ghost text-sm"
          >
            <Plus size={14} /> Add item
          </button>
        )}
      </div>
    </fieldset>
  );
}

/* ---------- per-page forms ---------- */

function HomeForm({ draft, setField, setDraft }) {
  return (
    <>
      <Group title="Hero">
        <TextField full label="Overline (small tag above title)" path="home.hero_overline" draft={draft} setField={setField} />
        <TextField label="Hero line 1" path="home.hero_line1" draft={draft} setField={setField} />
        <TextField label="Hero line 2 (italic)" path="home.hero_line2" draft={draft} setField={setField} />
        <TextArea label="Hero subtitle" path="home.hero_subtitle" draft={draft} setField={setField} rows={3} />
        <TextField label="Primary CTA label" path="home.cta_primary_label" draft={draft} setField={setField} />
        <TextField label="Secondary CTA label" path="home.cta_secondary_label" draft={draft} setField={setField} />
      </Group>

      <ArrayEditor
        title="Stats (max 3 shown)"
        path="home.stats"
        draft={draft}
        setDraft={setDraft}
        fields={[
          { key: "value", label: "Value (big number/text)" },
          { key: "label", label: "Label" },
          { key: "sub", label: "Sub-text", full: true },
        ]}
        addItem={() => ({ value: "", label: "", sub: "" })}
        minItems={1}
      />

      <Group title="Intro strip">
        <TextField label="Overline" path="home.intro_overline" draft={draft} setField={setField} />
        <TextField label="Link text" path="home.intro_link_text" draft={draft} setField={setField} />
        <TextField label="Heading line 1" path="home.intro_heading_line1" draft={draft} setField={setField} />
        <TextField label="Heading line 2" path="home.intro_heading_line2" draft={draft} setField={setField} />
        <TextArea label="Paragraph" path="home.intro_paragraph" draft={draft} setField={setField} rows={5} />
      </Group>

      <Group title="Process section">
        <TextField label="Overline" path="home.process_overline" draft={draft} setField={setField} />
        <TextField label="Heading" path="home.process_heading" draft={draft} setField={setField} />
        <TextField label="CTA button" path="home.process_cta" draft={draft} setField={setField} />
      </Group>

      <ArrayEditor
        title="Process cards (3 shown, icons fixed)"
        path="home.process_cards"
        draft={draft}
        setDraft={setDraft}
        fields={[
          { key: "title", label: "Title" },
          { key: "body", label: "Body", type: "textarea", full: true, rows: 3 },
        ]}
        addItem={() => ({ title: "", body: "" })}
      />

      <Group title="RPSY teaser">
        <TextField label="Overline" path="home.rpsy_overline" draft={draft} setField={setField} />
        <TextField label="Button label" path="home.rpsy_button" draft={draft} setField={setField} />
        <TextField full label="Heading" path="home.rpsy_heading" draft={draft} setField={setField} />
        <TextArea label="Paragraph" path="home.rpsy_paragraph" draft={draft} setField={setField} rows={4} />
      </Group>

      <Group title="Bottom CTA strip (green band)">
        <TextField label="Overline" path="home.cta_overline" draft={draft} setField={setField} />
        <TextField label="Button label" path="home.cta_button" draft={draft} setField={setField} />
        <TextField full label="Heading" path="home.cta_heading" draft={draft} setField={setField} />
        <TextArea label="Paragraph" path="home.cta_paragraph" draft={draft} setField={setField} rows={2} />
      </Group>
    </>
  );
}

function AboutForm({ draft, setField, setDraft }) {
  return (
    <>
      <Group title="Hero">
        <TextField full label="Overline" path="about.hero_overline" draft={draft} setField={setField} />
        <TextField label="Hero line 1" path="about.hero_line1" draft={draft} setField={setField} />
        <TextField label="Hero line 2" path="about.hero_line2" draft={draft} setField={setField} />
        <TextArea label="Hero paragraph" path="about.hero_paragraph" draft={draft} setField={setField} rows={5} />
      </Group>

      <ArrayEditor
        title="Company facts (shown as cards)"
        path="about.facts"
        draft={draft}
        setDraft={setDraft}
        fields={[
          { key: "key", label: "Label (e.g. CIN)" },
          { key: "value", label: "Value" },
        ]}
        addItem={() => ({ key: "", value: "", mono: false })}
      />

      <Group title="Mission section">
        <TextField label="Overline" path="about.mission_overline" draft={draft} setField={setField} />
        <TextField label="Heading" path="about.mission_heading" draft={draft} setField={setField} />
        <TextArea label="Paragraph 1" path="about.mission_paragraph1" draft={draft} setField={setField} rows={4} />
        <TextArea label="Paragraph 2" path="about.mission_paragraph2" draft={draft} setField={setField} rows={4} />
      </Group>

      <Group title="Timeline header">
        <TextField label="Overline" path="about.timeline_overline" draft={draft} setField={setField} />
        <TextField label="Heading" path="about.timeline_heading" draft={draft} setField={setField} />
      </Group>

      <ArrayEditor
        title="Timeline entries"
        path="about.timeline"
        draft={draft}
        setDraft={setDraft}
        fields={[
          { key: "year", label: "Year" },
          { key: "title", label: "Title" },
          { key: "body", label: "Body", type: "textarea", full: true, rows: 3 },
        ]}
        addItem={() => ({ year: "", title: "", body: "" })}
      />

      <Group title="Bottom CTA">
        <TextField full label="Heading" path="about.cta_heading" draft={draft} setField={setField} />
        <TextField label="Button label" path="about.cta_button" draft={draft} setField={setField} />
      </Group>
    </>
  );
}

function ProductsForm({ draft, setField, setDraft }) {
  return (
    <>
      <Group title="Hero">
        <TextField full label="Overline" path="products.hero_overline" draft={draft} setField={setField} />
        <TextField label="Hero line 1" path="products.hero_line1" draft={draft} setField={setField} />
        <TextField label="Hero line 2 (italic)" path="products.hero_line2" draft={draft} setField={setField} />
        <TextArea label="Hero paragraph" path="products.hero_paragraph" draft={draft} setField={setField} rows={5} />
      </Group>

      <Group title="Yarn families heading">
        <TextField label="Overline" path="products.families_overline" draft={draft} setField={setField} />
        <TextField label="Heading" path="products.families_heading" draft={draft} setField={setField} />
      </Group>

      <ArrayEditor
        title="Yarn types"
        path="products.yarn_types"
        draft={draft}
        setDraft={setDraft}
        fields={[
          { key: "name", label: "Name" },
          { key: "short", label: "Short (e.g. RPSY) — optional" },
          { key: "tag", label: "Tag (badge above name)" },
          { key: "desc", label: "Description", type: "textarea", full: true, rows: 3 },
        ]}
        addItem={() => ({ name: "", short: "", tag: "Speciality", desc: "" })}
      />

      <Group title="Count range section">
        <TextField label="Overline" path="products.count_overline" draft={draft} setField={setField} />
        <TextArea label="Paragraph" path="products.count_paragraph" draft={draft} setField={setField} rows={3} />
      </Group>

      <ArrayEditor
        title="Count range cells (max 6 shown nicely)"
        path="products.count_grid"
        draft={draft}
        setDraft={setDraft}
        fields={[
          { key: "range", label: "Range" },
          { key: "note", label: "Note" },
        ]}
        addItem={() => ({ range: "", note: "" })}
      />

      <Group title="Bottom CTA">
        <TextField full label="Heading" path="products.cta_heading" draft={draft} setField={setField} />
        <TextField label="Button label" path="products.cta_button" draft={draft} setField={setField} />
      </Group>
    </>
  );
}

function ContactForm({ draft, setField }) {
  return (
    <>
      <Group title="Hero">
        <TextField full label="Overline" path="contact.hero_overline" draft={draft} setField={setField} />
        <TextField full label="Hero heading" path="contact.hero_heading" draft={draft} setField={setField} />
        <TextArea label="Hero subtitle" path="contact.hero_subtitle" draft={draft} setField={setField} rows={2} />
      </Group>

      <Group title="Inquiry form copy">
        <TextField label="Overline" path="contact.form_overline" draft={draft} setField={setField} />
        <TextField label="Heading" path="contact.form_heading" draft={draft} setField={setField} />
        <TextArea label="Disclaimer (small text under form)" path="contact.form_disclaimer" draft={draft} setField={setField} rows={2} />
      </Group>

      <Group title="Company info (used in footer & contact card)">
        <TextField label="Legal name" path="company.name" draft={draft} setField={setField} />
        <TextField label="Former name" path="company.former_name" draft={draft} setField={setField} />
        <TextField label="CIN" path="company.cin" draft={draft} setField={setField} mono />
        <TextField label="Email" path="company.email" draft={draft} setField={setField} />
        <TextField label="Phone (display)" path="company.phone" draft={draft} setField={setField} />
        <TextField label="Phone (dial / tel: link)" path="company.phone_raw" draft={draft} setField={setField} placeholder="+917060101743" />
        <TextField full label="Address line 1" path="company.address_line1" draft={draft} setField={setField} />
        <TextField full label="Address line 2" path="company.address_line2" draft={draft} setField={setField} />
        <TextField full label="Address line 3" path="company.address_line3" draft={draft} setField={setField} />
      </Group>

      <Group title="Business hours">
        <TextField label="Weekday label" path="company.business_hours_label_weekday" draft={draft} setField={setField} />
        <TextField label="Weekday value" path="company.business_hours_value_weekday" draft={draft} setField={setField} mono />
        <TextField label="Sunday label" path="company.business_hours_label_sunday" draft={draft} setField={setField} />
        <TextField label="Sunday value" path="company.business_hours_value_sunday" draft={draft} setField={setField} mono />
        <TextField label="Overline" path="contact.business_hours_overline" draft={draft} setField={setField} />
        <TextField label="Heading" path="contact.business_hours_heading" draft={draft} setField={setField} />
        <TextArea label="Note (small text under hours)" path="contact.business_hours_note" draft={draft} setField={setField} rows={2} />
      </Group>
    </>
  );
}
